// Company Brain demo — данни за екрана „Решения“ (pure data, no logic).
// Чакащите решения продължават сюжетите от чат сценариите в scenarios.ts.

export type DecisionStatus = 'pending' | 'approved' | 'rejected';

export interface DecisionEvidence {
  label: string; // "Правило #2201"
  detail: string; // "Петя · 14.06.2025 · прилагано 12×, отменяно 1×"
}

export interface Decision {
  id: string;
  title: string;
  type: string; // "Правило" | "Плащане" | "Комуникация" | "Ново правило"
  client?: string;
  proposedBy: string; // "Company Brain" | име на колега
  date: string; // "05.07"
  summary: string; // кратко описание, plain text
  impact?: string; // какво се променя, ако се одобри
  evidence?: DecisionEvidence[];
  status: DecisionStatus;
  resolvedBy?: string; // за историята: "Мариела · 12.02.2026"
}

export const decisions: Decision[] = [
  // ── Чакащи одобрение ─────────────────────────────────────────────────────
  {
    id: 'dec-rule-2201',
    title: 'Архивиране на правило #2201 („гориво“)',
    type: 'Правило',
    client: 'Ромил ООД',
    proposedBy: 'Company Brain',
    date: '05.07',
    summary:
      'Правила #2201 и #4102 дават различно третиране на горивото от ОМВ и блокират автоматичното контиране. Предложение: #2201 се архивира в полза на #4102, което отразява договора за наем от ноември.',
    impact: 'Отблокира автоматичното контиране на фактурите от ОМВ (3 чакащи).',
    evidence: [
      { label: 'Правило #4102 (остава)', detail: 'Мариела · 05.11.2025 · прилагано 5×, потвърдено 5×' },
      { label: 'Правило #2201 (архив)', detail: 'Петя · 14.06.2025 · прилагано 12×, потвърдено 11×, отменяно 1×' },
    ],
    status: 'pending',
  },
  {
    id: 'dec-iban-megastroy',
    title: 'Нов IBAN за „Мега Строй“ — плащане 8 940 €',
    type: 'Плащане',
    client: 'Ромил ООД',
    proposedBy: 'Георги (потвърдил по телефон)',
    date: '05.07',
    summary:
      'Фактурата от „Мега Строй“ е с нов IBAN без официално уведомление — плащането беше блокирано. Георги е потвърдил новата сметка по телефон на познат номер. Чака финално одобрение за плащане и добавяне на IBAN-а като доверен.',
    impact: 'Плащането от 8 940 € се изпълнява; новият IBAN става доверен.',
    evidence: [
      { label: 'История', detail: '14 предишни фактури на стария IBAN · няма имейл за смяна' },
      { label: 'Потвърждение', detail: 'Георги · 05.07 · телефонно, на познат номер' },
    ],
    status: 'pending',
  },
  {
    id: 'dec-digest-romil',
    title: 'Седмичен дайджест за „Ромил ООД“',
    type: 'Комуникация',
    client: 'Ромил ООД',
    proposedBy: 'Company Brain',
    date: '05.07',
    summary:
      'Чернова на седмичния имейл (29.06 – 05.07): приходи 42 300 € (+8%), разходи 31 150 €, напомняне за пътния лист от 28.06 и бележка за задържаните позиции. Форматът следва предпочитанията на клиента — кратко, 3 секции.',
    impact: 'Имейлът се изпраща в петък сутрин от споделената поща.',
    evidence: [
      { label: 'Предпочитания за комуникация', detail: 'Мариела · 09.01.2026 · кратко, 3 секции, петък сутрин' },
    ],
    status: 'pending',
  },
  {
    id: 'dec-rule-artemis',
    title: 'Ново правило: Adobe абонамент — „Артемис Дизайн“',
    type: 'Ново правило',
    client: 'Артемис Дизайн ЕООД',
    proposedBy: 'Георги',
    date: '04.07',
    summary:
      'Месечният Adobe абонамент да се контира като разход за софтуерни услуги, с протокол по чл. 117 (доставчик от ЕС). Първо правило за новия клиент — засега прилагано ръчно 1×.',
    impact: 'Следващите фактури от Adobe се контират автоматично.',
    evidence: [{ label: 'Фактура Adobe · 07.2026', detail: 'ирландски VAT номер, валиден по VIES' }],
    status: 'pending',
  },

  // ── История ──────────────────────────────────────────────────────────────
  {
    id: 'dec-hist-4471',
    title: 'Правило #4471: разделяне на ДМА от гаранции',
    type: 'Правило',
    client: 'Ромил ООД',
    proposedBy: 'Company Brain',
    date: '12.02',
    summary: 'Смесените фактури за техника се разделят: ДМА отделно от гаранции и аксесоари.',
    status: 'approved',
    resolvedBy: 'Мариела · 12.02.2026',
  },
  {
    id: 'dec-hist-zapoved',
    title: 'Месечна обобщена заповед за редовните курсове',
    type: 'Правило',
    client: 'Ромил ООД',
    proposedBy: 'Петя',
    date: '22.01',
    summary: 'За шофьорите на редовния маршрут до Пловдив — месечна обобщена заповед вместо заповед за всеки курс.',
    status: 'approved',
    resolvedBy: 'Мариела · 22.01.2026',
  },
  {
    id: 'dec-hist-kurierski',
    title: 'Куриерски услуги като разход за продажби',
    type: 'Правило',
    client: 'Ромил ООД',
    proposedBy: 'Георги',
    date: '30.04',
    summary:
      'Предложение куриерските услуги да се водят разход за продажби. Отхвърлено — доминиращата практика (външни услуги, потвърдена 14×) остава единствена.',
    status: 'rejected',
    resolvedBy: 'Мариела · 30.04.2026',
  },
];
